<!--
  One state-driven page. The status label always renders; each affordance renders if and only if
  `can(<TRANSITION>)` says the associate may take that transition from the current state — the
  template asks the ported transition table and never a hand-written state list, which is what makes
  "only its permitted action" structural rather than something to keep in sync by hand.
-->
@if (visit) {
<div class="sv-negotiation" data-testid="site-visit-negotiation">
  <!--
    Header: which property, and where the negotiation stands - suppressed entirely when the host has
    already said both. See {@link identityInHost}.

    🔴 The attempt number is deliberately NOT rendered, anywhere. It used to appear twice - inline in
    the status pill, and on its own line when the host supplied the identity - on the reasoning that
    the round count is the one fact the queue row does not carry. Removed 2026-09-04: it is
    negotiation bookkeeping, and sitting it beside a date the site contact is being asked to approve
    made a normal second round read as though something had gone wrong. `visit.attempt` is still on
    the detail read, so this is a display decision and nothing was lost from the model.
    `site-visit-negotiation.component.spec.ts` guards it staying gone.
  -->
  @if (!identityInHost) {
  <div class="sv-negotiation__header">
    <div class="sv-negotiation__identity">
      @if (visit.propertyName) {
      <div class="sv-negotiation__property" data-testid="site-visit-property">
        {{ visit.propertyName }}
      </div>
      } @if (visit.address) {
      <div class="sv-negotiation__meta">{{ visit.address }}</div>
      } @if (visit.projectOrderId) {
      <div class="sv-negotiation__meta">{{ visit.projectOrderId }}</div>
      }
    </div>
    <span
      class="pill sv-negotiation__status"
      [ngClass]="pillClass"
      data-testid="site-visit-status-label"
    >
      <span class="material-icons">event</span>
      {{ label }}
    </span>
  </div>
  }

  <!--
    Passive copy: what the state means when there is nothing for the associate to do, and the summary
    of what has been agreed when there is. Every one of the twelve states is covered — the @switch has
    no fallback arm, so a state without a @case renders an empty panel rather than degrading.
  -->
  <div class="sv-negotiation__body" data-testid="site-visit-body">
    @switch (visit.status) {
    <!-- (1) NEEDS_AVAILABILITY — the associate's turn; the form is below -->
    @case ('NEEDS_AVAILABILITY') {
    <p class="sv-negotiation__intro" data-testid="site-visit-copy">
      Propose a date and an arrival time. The {{ awaitingParty }} will accept it or propose another
      time.
    </p>
    }

    <!-- (2) AWAITING_SITE_CONTACT_INFO — a creation state; nobody can be asked yet -->
    @case ('AWAITING_SITE_CONTACT_INFO') {
    <p class="sv-negotiation__intro" data-testid="site-visit-copy">
      We are waiting on the site contact's details before scheduling can begin. Nothing is needed from
      you yet.
    </p>
    }

    <!--
      (3) PENDING_SITE_CONTACT_APPROVAL — the proposal is out with the other party. The appointment
      is echoed in full, day AND time, because that whole pair is what is awaiting an answer now; the
      two-step flow could only show a day here.
    -->
    @case ('PENDING_SITE_CONTACT_APPROVAL') {
    <div class="sv-negotiation__state" data-testid="site-visit-copy">
      <div class="sv-negotiation__appointment" data-testid="site-visit-appointment">
        <div class="sv-negotiation__appointment-when">
          {{ proposedDayText }}@if (proposedTimeText) {&nbsp;· {{ proposedTimeText }}}
        </div>
        @if (visit.estimatedDuration) {
        <div class="sv-negotiation__appointment-meta">{{ visit.estimatedDuration }}</div>
        }
      </div>
      <p class="sv-negotiation__intro">
        Waiting on the {{ awaitingParty }}. We will notify you when they accept or propose another
        time.
      </p>
    </div>
    }

    <!--
      (4) REJECTED_ALTERNATIVES_PROVIDED — retired (Task 2); nothing transitions into it, and the
      alternatives list it named no longer exists. An orphaned row still lands here, and CANCEL is the
      only move the table leaves it, so the copy says so rather than promising a selection that will
      never open.
    -->
    @case ('REJECTED_ALTERNATIVES_PROVIDED') {
    <p class="sv-negotiation__intro" data-testid="site-visit-copy">
      This site visit was left part-scheduled by the previous scheduling flow and cannot go forward.
      Cancel it, and contact your project manager if the visit still needs to happen.
    </p>
    }

    <!--
      (5) PENDING_PA_APPROVAL — the associate's turn. The site contact countered; the appointment on
      the table is theirs, and the two answers to it are below.
    -->
    @case ('PENDING_PA_APPROVAL') {
    <div class="sv-negotiation__state" data-testid="site-visit-copy">
      <p class="sv-negotiation__intro">
        The {{ awaitingParty }} proposed a different time.
      </p>
      <div class="sv-negotiation__appointment" data-testid="site-visit-appointment">
        <div class="sv-negotiation__appointment-when">
          {{ proposedDayText }}@if (proposedTimeText) {&nbsp;· {{ proposedTimeText }}}
        </div>
        @if (visit.estimatedDuration) {
        <div class="sv-negotiation__appointment-meta">{{ visit.estimatedDuration }}</div>
        }
      </div>
    </div>
    }

    <!--
      (5a) PENDING_PA_SELECTION — retired; the state (5) replaced. Nothing transitions into it, and the
      alternatives it once asked the associate to pick from are gone from the model, so no selection
      will ever open here. RESCHEDULE is deliberately not legal either — nothing was ever agreed, no
      day and no arrival time — which leaves CANCEL as the whole of its exit, the same treatment (4)
      gets. This @case exists because the @switch has no fallback arm and an orphaned row must still
      say where it stands rather than render an empty panel.
    -->
    @case ('PENDING_PA_SELECTION') {
    <p class="sv-negotiation__intro" data-testid="site-visit-copy">
      The {{ awaitingParty }} offered alternative days under the previous scheduling flow, and those
      alternatives are no longer on record. This site visit cannot go forward — cancel it, and contact
      your project manager if the visit still needs to happen.
    </p>
    }

    <!--
      (6) APPROVED — retired (Task 2). A day was agreed under the two-phase flow with no arrival time
      ever settled, which is not an appointment under this one. RESCHEDULE is legal from here, so the
      copy points at the move that gets the row back into the live loop.
    -->
    @case ('APPROVED') {
    <div class="sv-negotiation__agreed" data-testid="site-visit-copy">
      <div class="sv-negotiation__agreed-line">
        <span class="material-icons">check_circle</span>
        Agreed: {{ agreedDayText }}@if (visit.estimatedDuration) {&nbsp;·
        {{ visit.estimatedDuration }}}
      </div>
      <p class="sv-negotiation__agreed-note">
        No arrival time was ever agreed. Reschedule to propose a date and a time together.
      </p>
    </div>
    }

    <!-- (7) PENDING_TIME_PROPOSAL — retired (Task 2); an orphaned row, with reschedule as its way out -->
    @case ('PENDING_TIME_PROPOSAL') {
    <div class="sv-negotiation__agreed" data-testid="site-visit-copy">
      <div class="sv-negotiation__agreed-line">
        <span class="material-icons">check_circle</span>
        Agreed: {{ agreedDayText }}
      </div>
      <p class="sv-negotiation__agreed-note">
        No arrival time was ever agreed. Reschedule to propose a date and a time together.
      </p>
    </div>
    }

    <!-- (8) PENDING_TIME_CONFIRMATION — retired (Task 2); kept only so an orphaned row still renders -->
    @case ('PENDING_TIME_CONFIRMATION') {
    <p class="sv-negotiation__intro" data-testid="site-visit-copy">
      A time of <b class="sv-negotiation__strong">{{ proposedTimeText }}</b> on
      <b class="sv-negotiation__strong">{{ agreedDayText }}</b> was left unconfirmed by the previous
      scheduling flow. Reschedule to propose a date and a time together.
    </p>
    }

    <!-- (9) TIME_CONFIRMED — exact arrival time locked -->
    @case ('TIME_CONFIRMED') {
    <div class="sv-negotiation__agreed" data-testid="site-visit-copy">
      <div class="sv-negotiation__agreed-line">
        <span class="material-icons">check_circle</span>
        Confirmed: {{ agreedDayText }}@if (confirmedTimeText) {&nbsp;·
        {{ confirmedTimeText }}}@if (visit.estimatedDuration) {&nbsp;·
        {{ visit.estimatedDuration }}}
      </div>
    </div>
    }

    <!-- (10) SITE_VISIT_COMPLETED — terminal -->
    @case ('SITE_VISIT_COMPLETED') {
    <p class="sv-negotiation__done" data-testid="site-visit-copy">
      <span class="material-icons">check_circle</span>
      This site visit is complete.
    </p>
    }

    <!-- (11) CANCELLED — terminal -->
    @case ('CANCELLED') {
    <p class="sv-negotiation__intro" data-testid="site-visit-copy">
      This site visit was cancelled. Contact your project manager if it needs to be rescheduled.
    </p>
    }
    }

    <!--
      🔴 Parties. `partiesVisible` is the ONLY condition this block renders under, and it requires the
      appointment to be agreed — `TIME_CONFIRMED`, or the `SITE_VISIT_COMPLETED` that follows it.
      Party phone numbers are revealed only on agreement, which is a security property rather than a
      UX choice. Do not add a second condition here or swap this for a `visit.parties` test: the getter
      is the single gate, and a projection that wrongly carried parties earlier is caught by it.
    -->
    @if (partiesVisible) {
    <div class="sv-negotiation__parties" data-testid="site-visit-parties">
      @if (visit.parties?.siteContactName || visit.parties?.siteContactPhone) {
      <div class="sv-negotiation__party">
        <span class="material-icons">call</span>
        <span class="sv-negotiation__party-label">Site contact</span>
        <span>{{ visit.parties?.siteContactName }}@if
          (visit.parties?.siteContactName && visit.parties?.siteContactPhone) {&nbsp;·
          }{{ visit.parties?.siteContactPhone }}</span
        >
      </div>
      } @if (visit.parties?.professionalAssociateName ||
      visit.parties?.professionalAssociatePhone) {
      <div class="sv-negotiation__party">
        <span class="material-icons">badge</span>
        <span class="sv-negotiation__party-label">On site</span>
        <span>{{ visit.parties?.professionalAssociateName }}@if
          (visit.parties?.professionalAssociateName &&
          visit.parties?.professionalAssociatePhone) {&nbsp;·
          }{{ visit.parties?.professionalAssociatePhone }}</span
        >
      </div>
      }
    </div>
    } @if (visit.arrivalPlan && !isTerminal) {
    <p class="sv-negotiation__plan" data-testid="site-visit-arrival-plan">
      "{{ visit.arrivalPlan }}"
    </p>
    }

    <!--
      Mock A3 — the post-visit report CURRENTLY on the visit. `SiteVisitSchedule` keeps this as ONE
      value, not per-attempt history: the next `COMPLETE`/`RESCHEDULE` replaces it, so this renders
      whatever the server has right now and nothing more. Gated on the two flags directly rather than
      on status, because a report can be sitting here at any of several states between one attempt's
      failure and the next one's outcome.

      Thumbnails are icon placeholders, not `<img>`s — these are documents already stored server-side,
      the read carries no format for them, and an iPhone access-issue photo has none anyway
      (`DocumentFormatEnum` has no HEIC entry). See `isPreviewableImage` for the ONE place this panel
      does attempt a real image preview: the files the PA is adding to THIS report, which are local and
      do carry a type.
    -->
    @if (visit.accessIssues || visit.concerns) {
    <div class="sv-negotiation__report" data-testid="site-visit-report">
      <div class="sv-negotiation__report-title">
        <span class="material-icons" aria-hidden="true">report_problem</span>
        Previous attempt —
        @if (visit.accessIssues && visit.concerns) { access issue &amp; concern } @else if
        (visit.accessIssues) { access issue } @else { concern }
      </div>
      @if (visit.completionNotes) {
      <p class="sv-negotiation__report-notes" data-testid="site-visit-report-notes">
        "{{ visit.completionNotes }}"
      </p>
      }
      @if ((visit.accessIssuePhotoIds?.length ?? 0) + (visit.concernPhotoIds?.length ?? 0) > 0) {
      <div class="sv-negotiation__thumbs">
        @for (id of visit.accessIssuePhotoIds; track id) {
        <span class="sv-negotiation__thumb" data-testid="site-visit-report-thumb">
          <span class="material-icons" aria-hidden="true">photo</span>
        </span>
        } @for (id of visit.concernPhotoIds; track id) {
        <span class="sv-negotiation__thumb" data-testid="site-visit-report-thumb">
          <span class="material-icons" aria-hidden="true">photo</span>
        </span>
        }
      </div>
      }
    </div>
    }
  </div>

  <!-- ===================== affordances, each gated on the transition table ===================== -->

  <!--
    PROPOSE_VISIT — the whole appointment in one submission.

    🔴 The date and the arrival time sit in ONE row and go out in ONE move. That is the change this
    form exists for: the retired flow put an AM/PM window here and asked for a clock time in a second
    round, days later, after the day had been agreed. Both fields are required — the handler refuses a
    proposal that names no time — and the arrival time is a dropdown over the whole day rather than a
    native time input, so the options are visible and countable (see `arrivalTimeOptions`).
  -->
  @if (can('PROPOSE_VISIT')) {
  <div class="sv-negotiation__form" data-testid="site-visit-availability-form">
    <div class="sv-negotiation__row">
      <div class="sv-negotiation__field">
        <label class="form-label" for="sv-availability-day">Date</label>
        <input
          id="sv-availability-day"
          type="date"
          class="input"
          [(ngModel)]="availabilityDay"
          data-testid="site-visit-availability-day"
        />
      </div>
      <div class="sv-negotiation__field">
        <label class="form-label" for="sv-availability-time">Arrival time</label>
        <select
          id="sv-availability-time"
          class="input sv-negotiation__time"
          [(ngModel)]="availabilityTime"
          data-testid="site-visit-availability-time"
        >
          @for (option of arrivalTimeChoices(availabilityTime); track option.value) {
          <option [value]="option.value">{{ option.label }}</option>
          }
        </select>
      </div>
      <div class="sv-negotiation__field">
        <label class="form-label" for="sv-availability-duration">Estimated duration</label>
        <input
          id="sv-availability-duration"
          type="text"
          class="input sv-negotiation__duration"
          [(ngModel)]="availabilityDuration"
          data-testid="site-visit-availability-duration"
        />
      </div>
    </div>
    <!--
      🔴 Static, and unguarded on purpose. This hint used to read
      `@if (visit.siteTimeZone) { ... ({{ visit.siteTimeZone }}) }`, which never rendered:
      `AssociateSiteVisitView` deliberately does NOT carry `siteTimeZone` — it argues the omission at
      length and `AssociateSiteVisitViewTest` pins the key set at twenty-three, so the guard was
      permanently false and the hint invisible in the running app while the spec's own fixture set the
      key by hand. The sentence is what carries the meaning here — the PA is typing a wall-clock time
      and needs to know which clock it is read against — so it is stated outright rather than gated on
      a value the wire will never send. Naming the zone would mean widening the projection, which is a
      design decision and not a template's to take.
    -->
    <p class="sv-negotiation__hint" data-testid="site-visit-availability-zone">
      Times are in the property's local zone.
    </p>
    <!--
      🔴 A warning, not a block. The submit stays enabled: an out-of-hours arrival is a real request,
      so the picker offers the whole day and this only says the choice is unusual. See
      `isOutsideWorkingHours`.
    -->
    @if (isOutsideWorkingHours(availabilityTime)) {
    <p class="sv-negotiation__warn" data-testid="site-visit-availability-time-warning">
      {{ timeText(availabilityTime) }} is outside the usual {{ timeRangeText }}. You can still send it.
    </p>
    }
    <div class="sv-negotiation__field">
      <label class="form-label" for="sv-availability-plan">
        Arrival plan (shared with the {{ awaitingParty }})
      </label>
      <textarea
        id="sv-availability-plan"
        rows="2"
        class="input"
        [(ngModel)]="availabilityArrivalPlan"
        data-testid="site-visit-availability-plan"
      ></textarea>
    </div>
    <button
      type="button"
      class="btn btn--primary"
      [disabled]="!canSubmitAvailability || isSubmitting"
      (click)="submitAvailability()"
      data-testid="site-visit-submit-availability"
    >
      Send Request
    </button>
  </div>
  }

  <!--
    PA_ACCEPT / PA_COUNTER — the two answers to the site contact's counter, at `PENDING_PA_APPROVAL`
    and nowhere else. Each is gated on its own transition rather than on the state, so the pair cannot
    drift from the table: `PA_ACCEPT` and `PA_COUNTER` have one legal source each and admit only the
    associate, which is why they are named per side.

    Accept is one tap and carries nothing — the server agrees the values already stored on the
    proposal, so there is no form to fill in and nothing this side could send that would change them.
  -->
  @if (can('PA_ACCEPT') || can('PA_COUNTER')) {
  <div class="sv-negotiation__form" data-testid="site-visit-respond-form">
    <div class="sv-negotiation__choices">
      @if (can('PA_ACCEPT')) {
      <button
        type="button"
        class="btn btn--primary"
        [disabled]="isSubmitting"
        (click)="acceptProposal()"
        data-testid="site-visit-accept"
      >
        Accept
      </button>
      } @if (can('PA_COUNTER')) {
      <button
        type="button"
        class="btn btn--secondary"
        [attr.aria-expanded]="counterOpen"
        [disabled]="isSubmitting"
        (click)="toggleCounter()"
        data-testid="site-visit-counter-toggle"
      >
        Propose Another Time
      </button>
      }
    </div>

    <!--
      Removed from the DOM rather than hidden, like every other conditional block here. The fields are
      seeded from the proposal on the table when the section opens, so the hint below is true: one edit
      is enough.
    -->
    @if (can('PA_COUNTER') && counterOpen) {
    <div class="sv-negotiation__counter" data-testid="site-visit-counter-form">
      <div class="sv-negotiation__row">
        <div class="sv-negotiation__field">
          <label class="form-label" for="sv-counter-day">Date</label>
          <input
            id="sv-counter-day"
            type="date"
            class="input"
            [(ngModel)]="counterDay"
            data-testid="site-visit-counter-day"
          />
        </div>
        <div class="sv-negotiation__field">
          <label class="form-label" for="sv-counter-time">Arrival time</label>
          <select
            id="sv-counter-time"
            class="input sv-negotiation__time"
            [(ngModel)]="counterTime"
            data-testid="site-visit-counter-time"
          >
            @for (option of arrivalTimeChoices(counterTime); track option.value) {
            <option [value]="option.value">{{ option.label }}</option>
            }
          </select>
        </div>
      </div>
      <p class="sv-negotiation__hint">Change either field, or both.</p>
      @if (isOutsideWorkingHours(counterTime)) {
      <p class="sv-negotiation__warn" data-testid="site-visit-counter-time-warning">
        {{ timeText(counterTime) }} is outside the usual {{ timeRangeText }}. You can still send it.
      </p>
      }
      <button
        type="button"
        class="btn btn--primary"
        [disabled]="!canSubmitCounter || isSubmitting"
        (click)="submitCounter()"
        data-testid="site-visit-submit-counter"
      >
        Send
      </button>
    </div>
    }
  </div>
  }


  <!--
    RESCHEDULE — move the agreed day. Gated on `can()` like every other affordance, so it appears at
    exactly the four states the transition table makes it legal from and nowhere else.

    Not a second date picker: the same `type="date"` control, the same arrival-time dropdown and the
    same day/arrivalTime/arrivalPlan payload as the proposal form above, through the same `dispatch`.
    What it adds is the reason, which this transition alone carries — and alone REQUIRES, so the PA's
    moves can be tracked. The label says so, and the submit stays disabled until the reason is non-blank; the
    check trims first, so spaces do not buy their way past a rule the server will enforce anyway.

    The reason is also BOUNDED, at the same number connect-service caps it at — `maxlength` plus a live
    counter, the shape the comments composer uses for the same server cap, so the PA sees the limit
    coming rather than meeting it as a failure toast after submitting. The over-length warning is its own
    branch: telling somebody who has picked a day and given a reason to "pick a day and say why" would be
    the frontend disagreeing with itself.

    🔴 It is also COLLAPSED behind a disclosure, at every state it is legal from. `TIME_CONFIRMED`
    renders the completion form as well, and this five-field exception path stacked above it dominated
    the report the PA had actually opened the panel to file. The three retired states carry no competing
    form, and collapsing there buys nothing on its own; it is done anyway, because a panel that changes
    shape between states for a reason the PA cannot see is worse than one that behaves the same
    everywhere. See `rescheduleOpen` for why it starts closed on every mount and why collapsing keeps
    whatever has been typed.
  -->
  @if (can('RESCHEDULE')) {
  <!--
    🔴 `site-visit-reschedule-form` stays on this OUTER element, and names the SECTION rather than the
    fields: it is what renders whenever the move is legal, which is what the presence checks in this
    component's spec and in `scheduling-page.component.spec.ts` are asking about. Moving the id inward
    onto the body would read tidier and would quietly turn those into "is it expanded?" — a question
    they do not mean to ask and would answer wrongly. The form's own presence is asserted through
    `site-visit-submit-reschedule` and `site-visit-reschedule-reason`, after opening.
  -->
  <div
    class="sv-negotiation__disclosure"
    [class.sv-negotiation__disclosure--open]="rescheduleOpen"
    data-testid="site-visit-reschedule-form"
  >
    <!--
      The trigger. It follows the related-records disclosure in `audit-timeline.component.html`: a
      real `<button type="button">` carrying `aria-expanded`, a chevron with an `--open` modifier,
      and a `toggleReschedule()` on the component — so it is focusable and operable by keyboard for
      free, with no key handler of our own that could fall out of step with the click one.

      One deliberate divergence from that source: it carries NO `aria-label`. The audit timeline’s
      chevron is icon-only, so its Expand/Collapse label is the only name it has; this button has
      visible text, and an `aria-label` would REPLACE that text as the accessible name with a bare
      "Expand" — a worse name than the one already there, and one the visible label no longer
      contains. So: the text names the action, `aria-expanded` carries the state, and the glyph is
      `aria-hidden` to keep it out of the name. No `aria-controls` either — the body it would point
      at does not exist while collapsed, and a dangling reference is worse than none.

      The copy is a question rather than an imperative on purpose. At two of the four states this row
      sits directly beneath the form the PA actually came to fill in, so it is an offer to be noticed
      and passed over, not a control competing for the next click.
    -->
    <button
      type="button"
      class="sv-negotiation__disclosure-btn"
      [attr.aria-expanded]="rescheduleOpen"
      (click)="toggleReschedule()"
      data-testid="site-visit-reschedule-toggle"
    >
      <span
        class="material-icons sv-negotiation__disclosure-chevron"
        [class.sv-negotiation__disclosure-chevron--open]="rescheduleOpen"
        aria-hidden="true"
        >chevron_right</span
      >
      Need to move the agreed day?
    </button>

    <!--
      Removed from the DOM rather than hidden, the way every other conditional block on this page is
      written. Nothing inside it changes with the collapse — same fields, same required marker, same
      counter, same disabled-submit guard. The only question this `@if` answers is whether the form
      is on screen.
    -->
    @if (rescheduleOpen) {
    <div class="sv-negotiation__disclosure-body">
      <p class="sv-negotiation__intro">
        Moving an agreed appointment sends the new date and time back to the {{ awaitingParty }} to
        approve, and gives up the one already agreed.
      </p>
      <div class="sv-negotiation__row">
        <div class="sv-negotiation__field">
          <label class="form-label" for="sv-reschedule-day">New date</label>
          <input
            id="sv-reschedule-day"
            type="date"
            class="input"
            [(ngModel)]="rescheduleDay"
            data-testid="site-visit-reschedule-day"
          />
        </div>
        <div class="sv-negotiation__field">
          <label class="form-label" for="sv-reschedule-time">Arrival time</label>
          <select
            id="sv-reschedule-time"
            class="input sv-negotiation__time"
            [(ngModel)]="rescheduleTime"
            data-testid="site-visit-reschedule-time"
          >
            @for (option of arrivalTimeChoices(rescheduleTime); track option.value) {
            <option [value]="option.value">{{ option.label }}</option>
            }
          </select>
        </div>
      </div>
      @if (isOutsideWorkingHours(rescheduleTime)) {
      <p class="sv-negotiation__warn" data-testid="site-visit-reschedule-time-warning">
        {{ timeText(rescheduleTime) }} is outside the usual {{ timeRangeText }}. You can still send it.
      </p>
      }
      <div class="sv-negotiation__field">
        <label class="form-label" for="sv-reschedule-reason">
          Why the agreed day is moving<span class="req" aria-hidden="true">*</span>
        </label>
        <input
          id="sv-reschedule-reason"
          type="text"
          class="input"
          aria-required="true"
          [maxlength]="maxReasonLength"
          [(ngModel)]="rescheduleReason"
          data-testid="site-visit-reschedule-reason"
        />
        <span class="sv-negotiation__char-count" data-testid="site-visit-reschedule-reason-count">
          {{ rescheduleReason.length }}/{{ maxReasonLength }}
        </span>
      </div>
      <div class="sv-negotiation__field">
        <label class="form-label" for="sv-reschedule-plan">
          Arrival plan (shared with the {{ awaitingParty }})
        </label>
        <textarea
          id="sv-reschedule-plan"
          rows="2"
          class="input"
          [(ngModel)]="rescheduleArrivalPlan"
          data-testid="site-visit-reschedule-plan"
        ></textarea>
      </div>
      <button
        type="button"
        class="btn btn--primary"
        [disabled]="!canSubmitReschedule || isSubmitting"
        (click)="submitReschedule()"
        data-testid="site-visit-submit-reschedule"
      >
        Reschedule Visit
      </button>
      @if (rescheduleReasonTooLong) {
      <p class="sv-negotiation__warn" data-testid="site-visit-reschedule-length-warning">
        Shorten the reason to {{ maxReasonLength }} characters or fewer.
      </p>
      } @else if (!canSubmitReschedule) {
      <p class="sv-negotiation__warn" data-testid="site-visit-reschedule-warning">
        Pick the new date and time, and say why it is moving.
      </p>
      }
    </div>
    }
  </div>
  }

  <!--
    Post-visit completion (Mocks A1-A3). One report; which segment is picked decides whether it rides
    COMPLETE or RESCHEDULE. Gated on `can('COMPLETE')` alone — COMPLETE and RESCHEDULE share the
    identical legal-from set (all four states at or after agreement) and both admit the associate
    unconditionally, so the two are always legal together for this actor; see `completionOutcome`'s
    own comment for the exact reasoning.
  -->
  @if (can('COMPLETE')) {
  <div class="sv-negotiation__form sv-negotiation__completion" data-testid="site-visit-completion-form">
    <div class="sv-negotiation__completion-title">How did the visit go?</div>

    <!--
      The segmented control (Mock A1). The primary button below follows it — "Submit Completion" vs
      "Report Failed Visit" — so a PA is never told "complete" for a visit that did not happen.
    -->
    <div class="sv-negotiation__seg" role="group" aria-label="Visit outcome">
      <button
        type="button"
        class="sv-negotiation__seg-btn"
        [class.sv-negotiation__seg-btn--on]="completionOutcome === 'COMPLETED'"
        [attr.aria-pressed]="completionOutcome === 'COMPLETED'"
        (click)="selectCompletionOutcome('COMPLETED')"
        data-testid="site-visit-completion-outcome-completed"
      >
        Completed
      </button>
      <button
        type="button"
        class="sv-negotiation__seg-btn"
        [class.sv-negotiation__seg-btn--on]="completionOutcome === 'FAILED'"
        [attr.aria-pressed]="completionOutcome === 'FAILED'"
        (click)="selectCompletionOutcome('FAILED')"
        data-testid="site-visit-completion-outcome-failed"
      >
        Couldn't Complete
      </button>
    </div>

    <!-- Access issues — the checkbox reveals its upload zone, and the photo is REQUIRED. -->
    <label class="sv-negotiation__check">
      <input
        type="checkbox"
        [checked]="completionAccessIssues"
        (change)="toggleCompletionAccessIssues()"
        data-testid="site-visit-completion-access-issue-check"
      />
      I had access issues
    </label>
    @if (completionAccessIssues) {
    <div
      class="sv-negotiation__dropzone"
      [class.sv-negotiation__dropzone--err]="accessIssuePhotoMissing"
      (dragover)="onCompletionDragOver($event)"
      (drop)="onAccessIssueFilesDropped($event)"
    >
      <label for="sv-access-issue-files" class="sv-negotiation__dropzone-label">
        <span class="material-icons" aria-hidden="true">photo_camera</span>
        <span>
          Add a photo of the access issue<span class="req" aria-hidden="true">*</span>
        </span>
        <span class="sv-negotiation__dropzone-hint">Drag a file here, or browse</span>
      </label>
      <input
        id="sv-access-issue-files"
        type="file"
        accept="image/*"
        multiple
        aria-required="true"
        (change)="onAccessIssueFilesSelected($event)"
        data-testid="site-visit-access-issue-files"
      />
      @if (completionAccessIssueFiles.length) {
      <div class="sv-negotiation__thumbs">
        @for (file of completionAccessIssueFiles; track file.name + file.lastModified) {
        <span class="sv-negotiation__thumb" data-testid="site-visit-access-issue-thumb">
          @if (isPreviewableImage(file)) {
          <img [src]="previewUrl(file)" alt="" />
          } @else {
          <span class="material-icons" aria-hidden="true">image</span>
          }
        </span>
        }
      </div>
      }
    </div>
    <!--
      🔴 Mock A2: the required-photo rule as a SERVER-shaped refusal, live, rather than only a
      disabled button — the rule is enforced in the handler and any other caller hits the same wall.
      `accessIssuePhotoMissing` is the same predicate `canSubmitCompletion` disables the button on, so
      this text and the disabled state can never disagree about why.
    -->
    @if (accessIssuePhotoMissing) {
    <p class="sv-negotiation__err" data-testid="site-visit-access-issue-photo-warning">
      <span class="material-icons" aria-hidden="true">error</span>
      A photo documenting the access issue is required.
    </p>
    }
    }

    <!-- Concerns — NOT photo-gated; a concern is often a judgement rather than something photographable. -->
    <label class="sv-negotiation__check">
      <input
        type="checkbox"
        [checked]="completionConcerns"
        (change)="toggleCompletionConcerns()"
        data-testid="site-visit-completion-concern-check"
      />
      I have concerns about the property
    </label>
    @if (completionConcerns) {
    <div
      class="sv-negotiation__dropzone"
      (dragover)="onCompletionDragOver($event)"
      (drop)="onConcernFilesDropped($event)"
    >
      <label for="sv-concern-files" class="sv-negotiation__dropzone-label">
        <span class="material-icons" aria-hidden="true">photo_camera</span>
        <span>Add a photo <span class="sv-negotiation__optional">(optional)</span></span>
        <span class="sv-negotiation__dropzone-hint">Concerns are not photo-gated</span>
      </label>
      <input
        id="sv-concern-files"
        type="file"
        accept="image/*"
        multiple
        (change)="onConcernFilesSelected($event)"
        data-testid="site-visit-concern-files"
      />
      @if (completionConcernFiles.length) {
      <div class="sv-negotiation__thumbs">
        @for (file of completionConcernFiles; track file.name + file.lastModified) {
        <span class="sv-negotiation__thumb" data-testid="site-visit-concern-thumb">
          @if (isPreviewableImage(file)) {
          <img [src]="previewUrl(file)" alt="" />
          } @else {
          <span class="material-icons" aria-hidden="true">image</span>
          }
        </span>
        }
      </div>
      }
    </div>
    }

    <!--
      Notes, capped at the same house limit `completionNotes` shares with the reschedule reason —
      `maxReasonLength` — and measured the same way: trimmed, on both sides.
    -->
    <div class="sv-negotiation__field">
      <label class="form-label" for="sv-completion-notes">
        Notes @if (completionOutcome === 'COMPLETED') {
        <span class="sv-negotiation__optional">(optional)</span>
        }
      </label>
      <textarea
        id="sv-completion-notes"
        rows="2"
        class="input"
        [maxlength]="maxReasonLength"
        placeholder="Anything the report writer should know…"
        [(ngModel)]="completionNotes"
        data-testid="site-visit-completion-notes"
      ></textarea>
      <span class="sv-negotiation__char-count" data-testid="site-visit-completion-notes-count">
        {{ completionNotes.length }}/{{ maxReasonLength }}
      </span>
    </div>

    <!--
      FAILED only: the replacement date, arrival time and reason — collected here because this branch
      applies RESCHEDULE, which requires all three. Distinct fields from the plain "move the agreed
      appointment" disclosure above; see `completionDay`'s own comment for why they must not share one.
    -->
    @if (completionOutcome === 'FAILED') {
    <div class="sv-negotiation__row">
      <div class="sv-negotiation__field">
        <label class="form-label" for="sv-completion-day">Replacement date</label>
        <input
          id="sv-completion-day"
          type="date"
          class="input"
          [(ngModel)]="completionDay"
          data-testid="site-visit-completion-day"
        />
      </div>
      <div class="sv-negotiation__field">
        <label class="form-label" for="sv-completion-time">Arrival time</label>
        <select
          id="sv-completion-time"
          class="input sv-negotiation__time"
          [(ngModel)]="completionTime"
          data-testid="site-visit-completion-time"
        >
          @for (option of arrivalTimeChoices(completionTime); track option.value) {
          <option [value]="option.value">{{ option.label }}</option>
          }
        </select>
      </div>
    </div>
    @if (isOutsideWorkingHours(completionTime)) {
    <p class="sv-negotiation__warn" data-testid="site-visit-completion-time-warning">
      {{ timeText(completionTime) }} is outside the usual {{ timeRangeText }}. You can still send it.
    </p>
    }
    <div class="sv-negotiation__field">
      <label class="form-label" for="sv-completion-reason">
        Why the visit could not be completed<span class="req" aria-hidden="true">*</span>
      </label>
      <input
        id="sv-completion-reason"
        type="text"
        class="input"
        aria-required="true"
        [maxlength]="maxReasonLength"
        [(ngModel)]="completionReason"
        data-testid="site-visit-completion-reason"
      />
      <span class="sv-negotiation__char-count" data-testid="site-visit-completion-reason-count">
        {{ completionReason.length }}/{{ maxReasonLength }}
      </span>
    </div>
    }

    <!-- The button names the outcome, so the PA is never told "complete" for a visit that did not happen. -->
    <button
      type="button"
      class="btn btn--primary"
      [disabled]="!canSubmitCompletion || isSubmitting"
      (click)="submitCompletion()"
      data-testid="site-visit-submit-completion"
    >
      {{ completionOutcome === 'FAILED' ? "Report Failed Visit" : "Submit Completion" }}
    </button>

    <!-- The server's refusal (Mock A2), for the case client-side validation did not already catch. -->
    @if (completionRefusal) {
    <p class="sv-negotiation__err" data-testid="site-visit-completion-refusal">
      <span class="material-icons" aria-hidden="true">error</span>
      {{ completionRefusal }}
    </p>
    } @else if (completionNotesTooLong) {
    <p class="sv-negotiation__warn" data-testid="site-visit-completion-notes-warning">
      Shorten the notes to {{ maxReasonLength }} characters or fewer.
    </p>
    } @else if (completionOutcome === 'FAILED' && completionReasonTooLong) {
    <p class="sv-negotiation__warn" data-testid="site-visit-completion-reason-warning">
      Shorten the reason to {{ maxReasonLength }} characters or fewer.
    </p>
    } @else if (completionOutcome === 'FAILED' && !canSubmitCompletion && !accessIssuePhotoMissing) {
    <p class="sv-negotiation__warn" data-testid="site-visit-completion-warning">
      Pick the replacement date and time, and say why the visit could not be completed.
    </p>
    }
  </div>
  }

  <!-- Recovery and calendar: not transitions, so gated on state directly -->
  <div class="sv-negotiation__actions">
    @if (canResendLink) {
    <button
      type="button"
      class="btn btn--secondary"
      [disabled]="isSubmitting"
      (click)="resendSiteContactLink()"
      data-testid="site-visit-resend-link"
    >
      Resend Link
    </button>
    }

    <!--
      The calendar link is a server-minted, token-addressed URL fetched on click. This side never
      composes a calendar path from a record id.

      🔴 `TIME_CONFIRMED` alone. `APPROVED` was offered here and the button could only ever fail from
      it: `SiteVisitCalendarContentBuilder.timeOnTheTable` reads `confirmedTime` and nothing else, the
      retired two-phase flow never set one at `APPROVED`, so `build()` returns empty and
      `SiteVisitResource.calendarLink` answers 409 NOTHING_TO_SCHEDULE. An offer that is guaranteed to
      error is worse than no offer, and a proposal is deliberately not a calendar entry.
    -->
    @if (!readOnly && visit.status === 'TIME_CONFIRMED') {
    <button
      type="button"
      class="btn btn--secondary"
      [disabled]="isSubmitting"
      (click)="openCalendarLink()"
      data-testid="site-visit-calendar-link"
    >
      Add to Calendar
    </button>
    }

    <!-- CANCEL — terminal, so it takes a second click to confirm -->
    @if (can('CANCEL')) { @if (!confirmingCancel) {
    <button
      type="button"
      class="btn btn--link"
      [disabled]="isSubmitting"
      (click)="startCancel()"
      data-testid="site-visit-cancel"
    >
      Cancel Visit
    </button>
    } @else {
    <div class="sv-negotiation__confirm-cancel" data-testid="site-visit-cancel-confirm">
      <span class="sv-negotiation__warn">Cancel this site visit? This cannot be undone.</span>
      <!--
        The reason, and it is required — the same rule, the same cap and the same trimmed measurement
        the reschedule reason is on, because the server validates the two with one method. The
        disclosure and the two-click confirm around it are deliberately unchanged: the defect this
        closes was that an irreversible move captured nothing, not that it was hard to find.
      -->
      <div class="sv-negotiation__field">
        <label class="form-label" for="sv-cancel-reason">
          Why the visit is being cancelled<span class="req" aria-hidden="true">*</span>
        </label>
        <input
          id="sv-cancel-reason"
          type="text"
          class="input"
          aria-required="true"
          [maxlength]="maxReasonLength"
          [(ngModel)]="cancelReason"
          data-testid="site-visit-cancel-reason"
        />
        <span class="sv-negotiation__char-count" data-testid="site-visit-cancel-reason-count">
          {{ cancelReason.length }}/{{ maxReasonLength }}
        </span>
      </div>
      <button
        type="button"
        class="btn btn--danger"
        [disabled]="!canConfirmCancel || isSubmitting"
        (click)="cancelVisit()"
        data-testid="site-visit-cancel-confirm-btn"
      >
        Cancel Visit
      </button>
      <button
        type="button"
        class="btn btn--link"
        [disabled]="isSubmitting"
        (click)="abandonCancel()"
        data-testid="site-visit-cancel-keep"
      >
        Keep Visit
      </button>
      @if (cancelReasonTooLong) {
      <p class="sv-negotiation__warn" data-testid="site-visit-cancel-length-warning">
        Shorten the reason to {{ maxReasonLength }} characters or fewer.
      </p>
      } @else if (!canConfirmCancel) {
      <p class="sv-negotiation__warn" data-testid="site-visit-cancel-warning">
        Say why the visit is being cancelled.
      </p>
      }
    </div>
    } }
  </div>
</div>
}
